import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import axios from "axios";
import { SITE } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  ...SITE.categories.map((c) => c.title),
  "Multiple products",
  "Other / General enquiry",
];

export default function ContactSection() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    category: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast.error("Please fill in name, phone and message.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        category: form.category || null,
        message: form.message.trim(),
      };
      await axios.post(`${API}/enquiries`, payload);
      setSent(true);
      toast.success("Enquiry received. Kunal will reach out shortly.");
      setForm({ name: "", phone: "", email: "", category: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try WhatsApp instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-40"
    >
      {/* Ambient glow */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[600px] w-[600px] rounded-full bg-[#1d52c4]/20 blur-[160px] pointer-events-none" />

      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 relative">
        <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-end mb-14">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="md:col-span-12 font-display text-4xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.98]"
          >
            Tell us about your space.{" "}
            <span className="italic font-light text-white/60">
              We&apos;ll bring the current.
            </span>
          </motion.h2>
        </div>

        <div className="grid md:grid-cols-12 gap-6 md:gap-10">
          {/* Left: form */}
          <motion.form
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            onSubmit={submit}
            noValidate
            data-testid="contact-form"
            className="md:col-span-7 glass-strong rounded-[28px] p-6 md:p-10"
          >
            <div className="grid md:grid-cols-2 gap-5">
              <Field
                label="Name"
                testid="input-name"
                value={form.name}
                onChange={set("name")}
                placeholder="Your full name"
                required
              />
              <Field
                label="Phone"
                testid="input-phone"
                value={form.phone}
                onChange={set("phone")}
                placeholder="+91 …"
                required
              />
              <Field
                label="Email (optional)"
                testid="input-email"
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="you@example.com"
              />
              <div className="flex flex-col">
                <label className="font-mono-tag text-[10px] uppercase text-white/40 mb-2">
                  Category
                </label>
                <select
                  data-testid="input-category"
                  value={form.category}
                  onChange={set("category")}
                  className="bg-transparent border-b border-white/[0.14] focus:border-[#22d3ee] outline-none py-3 text-white text-sm"
                >
                  <option value="" className="bg-[#0d1117]">
                    Select…
                  </option>
                  {categories.map((c) => (
                    <option key={c} value={c} className="bg-[#0d1117]">
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-6 flex flex-col">
              <label className="font-mono-tag text-[10px] uppercase text-white/40 mb-2">
                Your project *
              </label>
              <textarea
                data-testid="input-message"
                value={form.message}
                onChange={set("message")}
                rows={4}
                placeholder="Site type, quantities, timeline…"
                required
                className="bg-transparent border-b border-white/[0.14] focus:border-[#22d3ee] outline-none py-3 text-white text-sm resize-none"
              />
            </div>

            <div className="mt-10 flex flex-wrap gap-3 items-center justify-between">
              <p className="text-white/45 text-xs max-w-[38ch]">
                We reply within a business day. For urgent trade queries, please
                use WhatsApp.
              </p>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="group inline-flex items-center gap-2 rounded-full bg-white text-[#0d1117] pl-6 pr-2 py-2 text-[13px] font-medium hover:bg-white/90 transition-colors duration-300 disabled:opacity-60"
              >
                {sent ? "Sent" : "Send enquiry"}
                <span className="ml-1 grid h-9 w-9 place-items-center rounded-full bg-[#0d1117] text-white group-hover:bg-gradient-to-br group-hover:from-[#1d52c4] group-hover:to-[#22d3ee] transition-colors duration-300">
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ArrowUpRight className="h-4 w-4" />
                  )}
                </span>
              </button>
            </div>
          </motion.form>

          {/* Right: details */}
          <motion.aside
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.1 }}
            data-testid="contact-details"
            className="md:col-span-5 glass rounded-[28px] p-8 md:p-10 flex flex-col"
          >
            <div className="font-mono-tag text-[10px] uppercase text-white/40 mb-6">
              On the ground
            </div>
            <h3 className="font-display text-3xl md:text-4xl tracking-tighter leading-tight">
              {SITE.contactPerson}
              <span className="block text-white/50 text-base mt-1 font-satoshi">
                Proprietor, {SITE.short}
              </span>
            </h3>

            <div className="mt-10 space-y-6 text-sm">
              <Row label="Address">
                <a
                  data-testid="address-link"
                  href={SITE.mapsLink}
                  target="_blank"
                  rel="noreferrer"
                  className="text-white hover:text-[#22d3ee] transition-colors duration-300"
                >
                  {SITE.address}
                </a>
              </Row>
              <Row label="Phone">
                <a
                  data-testid="phone-link"
                  href={`tel:${SITE.phone.replace(/\s/g, "")}`}
                  className="text-white hover:text-[#22d3ee] transition-colors duration-300"
                >
                  {SITE.phone}
                </a>
              </Row>
              <Row label="WhatsApp">
                <a
                  data-testid="whatsapp-link"
                  href={`https://wa.me/${SITE.phoneRaw}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.preventDefault();
                    const url = `https://wa.me/${SITE.phoneRaw}`;
                    const w = window.open(url, "_blank", "noopener,noreferrer");
                    if (!w) window.location.href = url;
                  }}
                  className="text-[#25d366] hover:text-white transition-colors duration-300"
                >
                  Chat with Kunal →
                </a>
              </Row>
              <Row label="Hours">
                <div className="space-y-1">
                  {SITE.hours.map((h) => (
                    <div key={h.day} className="text-white">
                      <span className="text-white/50 mr-2">{h.day}</span>
                      {h.time}
                    </div>
                  ))}
                </div>
              </Row>
            </div>

            <div className="mt-10 relative rounded-2xl overflow-hidden h-48 border border-white/[0.07]">
              <iframe
                title="Giriraj Enterprises location"
                src={SITE.mapsEmbed}
                className="w-full h-full map-dark"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <a
                href={SITE.mapsLink}
                target="_blank"
                rel="noreferrer"
                className="absolute inset-0"
                aria-label="Open in Google Maps"
              />
            </div>
          </motion.aside>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", required, testid }) {
  return (
    <div className="flex flex-col">
      <label className="font-mono-tag text-[10px] uppercase text-white/40 mb-2">
        {label} {required && "*"}
      </label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        className="bg-transparent border-b border-white/[0.14] focus:border-[#22d3ee] outline-none py-3 text-white text-sm placeholder:text-white/25"
      />
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-[110px_1fr] items-start gap-4 border-t border-white/[0.06] pt-4">
      <span className="font-mono-tag text-[10px] uppercase text-white/40 pt-0.5">
        {label}
      </span>
      <div>{children}</div>
    </div>
  );
}
